import {
  Component,
  EventEmitter,
  inject,
  Injectable,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NgbCalendar, NgbDate, NgbDatepicker, NgbDatepickerI18n, NgbDateStruct, NgbInputDatepicker, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import {DateReformatSharedService} from '../../../../services/date-reformat-shared.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgIf, NgClass } from '@angular/common';

const I18N_VALUES = {
  keys: {
    weekdays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
    months: ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'],
  },
};

// Define a service holding the language. You probably already have one if your app is i18ned. Or you could also
// use the Angular LOCALE_ID value
@Injectable()
export class KeyLang {
  language = 'keys';
}

// Define custom service providing the months and weekdays translations
@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {

  private _i18n = inject(KeyLang);

  constructor(
    private translate: TranslateService,
  ) {
    super();
  }

  getWeekdayLabel(weekday: number): string {
    return this.translate.instant('CALENDAR.WEEKDAYS.' + I18N_VALUES[this._i18n.language].weekdays[weekday - 1]);
  }

  getMonthShortName(month: number): string {
    return this.translate.instant('CALENDAR.MONTHS.' + I18N_VALUES[this._i18n.language].months[month - 1]);
  }

  getMonthFullName(month: number): string {
    return this.getMonthShortName(month);
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}


@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [KeyLang, { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }],
    standalone: true,
    imports: [
        NgIf,
        NgClass,
        NgbDatepickerModule,
        MatTooltipModule,
        TranslateModule,
    ],
})
export class CalendarComponent implements OnInit {

  @ViewChild('hideput') hideput;
  /**
   * datas to get from the parent component
   * ***************************************
   * @d            --> For classic calendar input only, link to input with #d_value="ngbDatepicker"
   * Used for ranged calendar :
   * @isRangeInput --> Allow access to range calendar. True = ranged calendar ; false = classic calendar. By default : undefined.
   * @minDate      --> Get the minimal date authorize on picker. Default : undefined. If set, value = { year: value, month: value, day: value }
   * @maxDate      --> Get the maximum date authorize on picker. Default : undefined. If set, value = { year: value, month: value, day: value }
   * @isWeekendDisabled --> Set if weekends need to be disabled. Default : undefined. If set, isDisabled will make the work
   */
  @Input() d;
  @Input() isRangeInput;
  @Input() minDate;
  @Input() maxDate;
  @Input() isWeekendDisabled;
  @Input() isDisplayedRange;
  @Input() isContainer = false;
  @Input() dateSelectedByUser = {year: 2021, month: 11, day:26};
  @Input() hideButton = false;
  @Input() isUserReselect = false;
  @Input() isForcedDate:boolean=false;
  @Input() room;
  @Input() deleteStartDate: boolean;
  @Input() deleteEndDate: boolean;

  /**
   * datas sent to the parent component
   * ***********************************
   * Use for ranged calendar
   * @dates     --> send a json object with debut date and end date. Format : { start: value, end: value }
   * @startDate --> send a json object on start date selection with debut date. Format : {startdate: value}
   * @endDate   --> send a json object on end date selection with end date. Format : {enddate: value}
   */
  @Output() dates = new EventEmitter<any>();
  @Output() startDate = new EventEmitter<any>();
  @Output() endDate = new EventEmitter<any>();
  @Output() selectedDate = new EventEmitter<any>();
  @Output() closeCalendar = new EventEmitter<any>();

  /**
   * Component's datas to works properly
   * ***********************************
   * @fromDate     --> Default : undefined. If not undefined : Duration begin date.
   * @toDate       --> Default : undefined.  If not undefined :  Duration ending date.
   * @startingDate --> Save the reformat fromDate value. Used to send the begin date to the parent component.
   * @endingDate   --> Save the reformat toDate value. User to send the ending date to the parent component.
   * @hoveredDate  --> Use to stylise the calendar range. Isn't return to the parent because it has no stacked value.
   * @dateNav      --> Allow navigateTo() to be effective. Want a Json object : {year: value, month: value}
   * @navToDate    --> Default : Undefined. Set by calling addMonthsToDate() from parent component.
   *                   Become a JsonObject : {year: value, month: value}. User by dateNav during navigateTo.
   * @calendarMinDate   --> Set the min date the user can book.
   *                        In range, it's used as a "tmp" variable in order to keep minDate from the parent component
   * @calendarMaxDate   --> Set the max date the user can book.
   *                        In range, it's used as a "tmp" variable in order to keep maxDate from the parent component
   * @isDisabled        --> used by markDisabled in HTML. Set OnInit.
   */
  fromDate;
  toDate;
  startingDate;
  endingDate;
  hoveredDate;
  @ViewChild('hideput') dateNav: NgbDatepicker;
  navToDate;
  calendarMinDate;
  calendarMaxDate;
  @ViewChild('hideput') rangeInput: NgbInputDatepicker;
  compSelectedDate = {
    year: null,
    month: null,
    day: null,
  };
  isMobile = false;

  isDisabled = (date: NgbDate) => {
    if (this.isWeekendDisabled) {
      const tmpDate = new Date(date.year, date.month - 1, date.day);
      if (tmpDate.getDay() === 6 || tmpDate.getDay() === 0) {
        return true;
      }
    }
  }

  constructor(
    private calendar: NgbCalendar,
    private dateFactory: DateReformatSharedService,
    private mobileDetector: DeviceDetectorService,
  ) {}

  ngOnInit(): void {
    this.calendarMinDate = this.minDate;
    this.calendarMaxDate = this.maxDate;
    this.isMobile = this.mobileDetector.isMobile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deleteStartDate && changes.deleteStartDate.currentValue) {
      this.startingDate = this.fromDate = undefined;
    }

    if (changes.deleteEndDate && changes.deleteEndDate.currentValue) {
      this.endingDate = this.toDate = undefined;
      this.disableDateOnSelect();

    }
  }

  // Default function of the ranged calendar
  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
  }

  /**
   * This is the function called when a date is picked
   * This is where we emit Output() ( dates, startDate & endDate )
   * first "if" disabled range effect and turn the ranged calendar into a single-date select calendar
   */
  onDateSelection(date: NgbDate) {
    if (!this.isUserReselect) {
      if (this.isDisplayedRange && this.fromDate && this.fromDate.equals(date)) {
        this.toDate = date;
      } else {
        if (!this.fromDate && !this.toDate) {
          this.fromDate = date;
        }
        else if(!this.fromDate && this.toDate){
          this.fromDate = date;
          this.startingDate = this.dateFactory.format(this.fromDate);
          this.endingDate = this.toDate = undefined;
        }
        else if (this.fromDate && !this.toDate && date && date.after(this.fromDate)) {
          this.toDate = date;
        }
        else {
          this.toDate = null;
          this.fromDate = date;
        }
      }

      if (this.fromDate) {
        this.startingDate = this.dateFactory.format(this.fromDate);
        this.startDate.emit({startdate: this.startingDate});
        if (!this.isDisplayedRange && (this.toDate === null || this.toDate === undefined)) {
          this.disableDateOnSelect();
        }
      }
      if (this.toDate) {
        this.endingDate = this.dateFactory.format(this.toDate);
        this.endDate.emit({enddate: this.endingDate});
      }
      if (this.startingDate && this.endingDate) {
        this.dates.emit({ start: this.startingDate, end: this.endingDate});
        this.startingDate = undefined;
        this.endingDate = undefined;
        this.calendarMinDate = this.minDate;
        if (!this.isDisplayedRange && !this.isMobile && !this.isContainer) {
          this.rangeInput.close();
        }
      }

    } else {
      this.compSelectedDate = date;
      this.selectedDate.emit(date);
    }
  }

// Theses function are for style purpose only

  isStartDate(date) {
    if (date.equals(this.fromDate) ||
      (this.isUserReselect && date.equals(this.minDate))) {
      return date ;
    }
  }

  isEndDate(date: NgbDate) {
    if (date.equals(this.toDate) ||
      (this.isUserReselect && date.equals(this.maxDate))) {
      return date ;
    }
  }

  isHoverDate(date: NgbDate) {
    if ( this.startingDate !== undefined && date.equals(this.hoveredDate) && date.after(this.fromDate)) {
      return date ;
    }
    return ;
  }

  /**
   * Call this function from a parent component if you need a month interval between start and end dates.
   * Set the number of months the calendar navigateTo
   */
  addMonthsToDate(n: number) {
    const tmp = new Date(this.dateFactory.makeEngDateFormat(this.startingDate));
    const days = tmp.getDate();
    tmp.setMonth(tmp.getMonth() + +n);
    if (tmp.getDate() !== days) {
      tmp.setDate(0);
    }
    return this.navToDate = {year: tmp.getFullYear(), month: tmp.getMonth() + 1};
  }


  // Get back to current day, works only if the user selected a whole start-end booking
  backToCurrentDay() {
    this.dateNav.navigateTo(this.calendar.getToday());
  }

  // Refresh all informations as if the user never selected anything
  clear() {
    this.endingDate = this.startingDate =  this.fromDate = this.toDate =  undefined;
    this.calendarMinDate = this.minDate;
    this.startDate.emit({startdate: undefined});
    this.endDate.emit({enddate: undefined});
    this.dates.emit({ start: this.startingDate, end: this.endingDate});
    this.dateNav.navigateTo(this.calendar.getToday());
  }

  disableDateOnSelect() {
    // Navigate to mindate available with restricted mode
     this.dateNav.navigateTo(this.navToDate);
    // Disable dates before the min available date
    const tmpdate = new Date(this.navToDate.year, this.navToDate.month -1, this.fromDate.day);
    this.calendarMinDate = this.calendar.getToday();
    this.calendarMinDate.day = tmpdate.getUTCDate();
    this.calendarMinDate.month = tmpdate.getUTCMonth() + 1;
    this.calendarMinDate.year = tmpdate.getUTCFullYear();
  }

  showRangeCalendar() {
    this.rangeInput.toggle();
  }

  /**
   * Return the content of the tooltip
   * @param date
   */
  getTooltipContent(date: any) {
    const selectedDate = new Date(date.year, date.month -1, date.day);
    const maxDate = new Date(this.maxDate.year,this.maxDate.month -1,this.maxDate.day)
    if (selectedDate > maxDate) return "CALENDAR.OWNER_DISABLED_DATES";
    if (selectedDate.getDay() === 6 || selectedDate.getDay() === 0) return "CALENDAR.DATE.WEEK_END";
    if (!this.isUserReselect) {
      const minDate = new Date(this.minDate.year,this.minDate.month -1,this.minDate.day)
      if (selectedDate.getTime() < minDate.getTime()) return 'CALENDAR.DATE.BEFORE';
      if (this.startingDate) {
        const mindate2 = new Date(this.calendarMinDate.year, this.calendarMinDate.month -1, this.calendarMinDate.day);
        if (selectedDate.getTime() < mindate2.getTime()) {
          return 'CALENDAR.DATE.MIN_RESERVATION';
        }

      }
    } else {
      if (!this.isForcedDate) {
        const minDate = new Date(this.minDate.year,this.minDate.month -1,this.minDate.day)
        const maxDate = new Date(this.maxDate.year,this.maxDate.month -1,this.maxDate.day)
        if (selectedDate.getTime() < minDate.getTime() || selectedDate.getTime() > maxDate.getTime()) return "CALENDAR.OWNER_DISABLED_DATES";
      }
    }
  }


  isSelectedDate(date: any, adminView: boolean ) {
    if (adminView) {
      return (new Date(date.year, date.month - 1, date.day)).getTime() === (new Date(this.dateSelectedByUser.year, this.dateSelectedByUser.month - 1, this.dateSelectedByUser.day)).getTime()
    }
    return (new Date(date.year, date.month - 1, date.day)).getTime() === (new Date(this.compSelectedDate.year, this.compSelectedDate.month - 1, this.compSelectedDate.day)).getTime()
  }

  /**
   *
   * @param date
   * Return true if date is >= minDate & date <= maxDate
   * Only used in HTML to style date available (for booking incorrect date only)
   */
  isPreSetDate(date: any) {
    if (!this.isForcedDate) {
      const d = new Date(date.year, date.month - 1, date.day);
      const minD =  new Date(this.calendarMinDate.year, this.calendarMinDate.month - 1, this.calendarMinDate.day);
      const maxD =  new Date(this.calendarMaxDate.year, this.calendarMaxDate.month - 1, this.calendarMaxDate.day);
      if (d.getTime() >= minD.getTime() && d.getTime() < maxD.getTime()) {
        return true;
      }
    }

  }

  transformToMonth(days) {
    return Math.round(days / 30);
  }

  public isLastDay(date): boolean {
    const dDate = new Date(date.year, date.month - 1, date.day);
    const d = new Date(date.year, date.month - 1, date.day + 1);
    return dDate.getMonth() != d.getMonth();

  }

  public getDateDay(date): string {
    if (this.isMobile) {
      const d = new Date(date.year, date.month - 1, date.day);
      return 'day_' + d.getDay();
    }
    return '';

  }

}
